<template>
  <div>
    <CollectionView endpoint="admin/server-addons" default-order="createdAt:desc" v-model:reload="reload">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between w-100">
          <h4 class="mb-0">{{ translate("server_addons") }}</h4>
          <ButtonView type="normal" color="primary" @click="creating = true">
            {{ translate("create") }}
          </ButtonView>
        </div>
      </template>
      <template v-slot:content="{ items }">
        <div class="card">
          <div class="table-responsive">
            <div class="table">
              <div class="table-header">
                <div class="table-header-cell">{{ translate("type") }}</div>
                <div class="table-header-cell">{{ translate("version") }}</div>
                <div class="table-header-cell">{{ translate("details") }}</div>
                <div class="table-header-cell">{{ translate("instructions") }}</div>
                <div class="table-header-cell">{{ translate("download") }}</div>
                <div class="table-header-cell">{{ translate("created_at") }}</div>
                <div class="table-header-cell">{{ translate("actions") }}</div>
              </div>
              <div class="table-body">
                <div class="table-row" v-for="item in items" :key="item.id">
                  <div class="table-column">
                    <img class="img-fluid" :src="item.communityType.iconUrl" style="max-height: 1.4em; max-width: 1.4em"/>
                  </div>
                  <div class="table-column">
                    {{ item.version }}
                  </div>
                  <div class="table-column">
                    <ButtonView type="soft" color="primary" size="small" @click="viewingDetails = item">
                      <i class="fas fa-list-ul"></i>
                    </ButtonView>
                  </div>
                  <div class="table-column">
                    <ButtonView type="soft" color="primary" size="small" @click="viewingInstructions = item">
                      <i class="fas fa-list-ul"></i>
                    </ButtonView>
                  </div>
                  <div class="table-column">
                    <a v-if="item.fileUrl" class="btn btn-normal btn-primary small" :href="item.fileUrl" target="_blank">
                      <i class="fas fa-download"></i>
                    </a>
                  </div>
                  <div class="table-column">
                    {{ utilities.formatDatetime(item.createdAt) }}
                  </div>
                  <div class="table-column">
                    <div class="d-flex align-items-center g-2">
                      <ButtonView type="soft" color="primary" size="small" @click="editing = item">
                        <i class="fas fa-edit"></i>
                      </ButtonView>
                      <TargetedModalHttpFormView size="small" :endpoint="utilities.path('admin/server-addons/@', item.id)" method="delete" @success="reload = true">
                        <template v-slot:target="{ show }">
                          <ButtonView type="soft" color="danger" size="small" @click="show">
                            <i class="fas fa-trash"></i>
                          </ButtonView>
                        </template>
                        <template v-slot:form="{ submit, loading, hide }">
                          <SpinnerView :active="loading" color="danger"/>
                          <div class="d-flex align-items-center g-4">
                            <ButtonView type="normal" color="danger" class="w-100" @click="submit">
                              {{ translate("delete") }}
                            </ButtonView>
                            <ButtonView type="normal" color="secondary" class="w-100" @click="hide">
                              {{ translate("cancel") }}
                            </ButtonView>
                          </div>
                        </template>
                      </TargetedModalHttpFormView>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </CollectionView>
    <ModalView :active="viewingDetails !== undefined" @update:active="viewingDetails = undefined">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between">
          <h3 class="mb-0 d-flex align-items-center g-3">
            <img :src="viewingDetails.communityType.iconUrl" style="width: 1em; max-height: 1em"/>
            {{ viewingDetails.version }}
          </h3>
          <ModalCloseButtonView @click="viewingDetails = undefined"/>
        </div>
      </template>
      <template v-slot:body>
        <RichTextView :content="viewingDetails.details" :features="detailsFeatures"/>
      </template>
    </ModalView>
    <ModalView :active="viewingInstructions !== undefined" @update:active="viewingInstructions = undefined">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between">
          <h3 class="mb-0 d-flex align-items-center g-3">
            <img :src="viewingInstructions.communityType.iconUrl" style="width: 1em; max-height: 1em"/>
            {{ viewingInstructions.version }}
          </h3>
          <ModalCloseButtonView @click="viewingInstructions = undefined"/>
        </div>
      </template>
      <template v-slot:body>
        <RichTextView :content="viewingInstructions.instructions" :features="detailsFeatures"/>
      </template>
    </ModalView>
    <ModalView size="large" :active="creating || editing !== undefined" @update:active="creating = false; editing = undefined">
      <template v-slot:body>
        <HttpFormView :endpoint="creating ? 'admin/server-addons' : 'admin/server-addons/' + editing?.id" :method="creating ? 'post' : 'put'" :content="editing ? { communityType: editing.communityType.key, version: editing.version, details: editing.details, instructions: editing.instructions, fileIdentifier: editing.fileIdentifier, fileUrl: editing.fileUrl } : undefined" @success="creating = false; editing = undefined; reload = true">
          <template v-slot:form="{ content, errors, submit, loading }">
            <div class="d-flex flex-column g-4">
              <HttpSelectInputView :title="translate('type')" :errors="errors.communityType" required endpoint="admin/communities/types" v-model:value="content.communityType">
                <template v-slot:option="{ option }">
                  <div class="d-flex align-items-center g-2">
                    <img :src="option.imageUrl" style="width: 1em; max-height: 1em"/>
                    <span>{{ option.title }}</span>
                  </div>
                </template>
              </HttpSelectInputView>
              <InputView type="text" :title="translate('version')" required :errors="errors.version" v-model:value="content.version"/>
              <FileInputView :title="translate('file')" :extensions="['zip', 'jar']" :errors="errors.file" :value="content.fileIdentifier ? { identifier: content.fileIdentifier, url: content.fileUrl } : undefined" @update:value="content.fileIdentifier = $event?.identifier; content.fileUrl = $event?.url" endpoint="admin/server-addons/files" method="post"/>
              <RichTextInputView :title="translate('details')" required :errors="errors.details ? Array.isArray(errors.details) ? errors.details : [translate('richText.format.invalid')] : undefined" :features="detailsFeatures" v-model:value="content.details"/>
              <RichTextInputView :title="translate('instructions')" required :errors="errors.instructions ? Array.isArray(errors.instructions) ? errors.instructions : [translate('richText.format.invalid')] : undefined" :features="detailsFeatures" v-model:value="content.instructions"/>
              <div class="d-flex align-items-center g-4">
                <ButtonView type="normal" color="primary" class="w-100" @click="submit">
                  {{ translate("submit") }}
                </ButtonView>
                <ButtonView type="normal" color="secondary" class="w-100" @click="creating = false; editing = undefined">
                  {{ translate("cancel") }}
                </ButtonView>
              </div>
            </div>
            <SpinnerView :active="loading" color="primary"/>
          </template>
        </HttpFormView>
      </template>
    </ModalView>
  </div>
</template>

<script>
import {
  CollectionView,
  RichTextInputView,
  HttpSelectInputView,
  SpinnerView,
  HttpFormView,
  utilities,
  richTextFeatures,
  RichTextView,
  TargetedModalHttpFormView,
  FileInputView,
  ButtonView,
  InputView,
  ModalView,
  ModalCloseButtonView
} from "@niftybit/vue";

export default {
  name: "ServerAddonsView",
  components: {
    ModalCloseButtonView,
    SpinnerView, RichTextInputView, InputView, FileInputView, HttpSelectInputView, ModalView, ButtonView, TargetedModalHttpFormView, CollectionView, RichTextView, HttpFormView},
  props: {
    communityId: String
  },
  data() {
    return {
      reload: false,
      viewingDetails: undefined,
      viewingInstructions: undefined,
      creating: false,
      editing: undefined
    }
  },
  computed: {
    utilities() {
      return utilities
    },
    detailsFeatures() {
      return [
        richTextFeatures.paragraph.create(),
        richTextFeatures.text.create(),
        richTextFeatures.hardBreak.create(),
        richTextFeatures.heading.create(),
        richTextFeatures.textStyle.create(),
        richTextFeatures.codeBlock.create(),
        richTextFeatures.bulletList.create(),
        richTextFeatures.orderedList.create(),
        richTextFeatures.infoBox.create(),
        richTextFeatures.blockQuote.create(),
        richTextFeatures.textAlign.create(),
        richTextFeatures.code.create(),
        richTextFeatures.strikethrough.create(),
        richTextFeatures.bold.create(),
        richTextFeatures.underline.create(),
        richTextFeatures.italic.create(),
        richTextFeatures.link.create(),
        richTextFeatures.videoEmbed.create(),
      ];
    }
  }
}
</script>